<script setup>
import { computed, watch, ref } from "vue";

const props = defineProps({
  currentAmount: {
    type: Number,
    default: 0,
  },
  targetAmount: {
    type: Number,
    default: 0,
  },
});

// ピカピカ拡大縮小エフェクト用の状態
const isPulsing = ref(false);

// デバッグ: propsの変更を監視
watch(
  () => [props.currentAmount, props.targetAmount],
  (newValues, oldValues) => {
    const [newCurrentAmount, newTargetAmount] = newValues || [0, 0];
    const [oldCurrentAmount, oldTargetAmount] = oldValues || [undefined, undefined];
    console.log("=== CrowdfundingProgressBar: props更新 ===");
    console.log("currentAmount: " + (oldCurrentAmount ?? "初期値") + " → " + newCurrentAmount);
    console.log("targetAmount: " + (oldTargetAmount ?? "初期値") + " → " + newTargetAmount);
    console.log(
      "型確認 - currentAmount:",
      typeof newCurrentAmount,
      "targetAmount:",
      typeof newTargetAmount
    );

    // 金額が増加した時にピカピカ拡大縮小エフェクトを発動
    if (
      oldCurrentAmount !== undefined &&
      newCurrentAmount > oldCurrentAmount &&
      newCurrentAmount > 0
    ) {
      triggerPulseEffect();
    }
  },
  { immediate: true }
);

// ピカピカ拡大縮小エフェクトを発動
const triggerPulseEffect = () => {
  isPulsing.value = true;
  // エフェクト完了後にリセット
  setTimeout(() => {
    isPulsing.value = false;
  }, 5000);
};

// 進捗率を計算（100%を超えても表示可能）
const progressPercentage = computed(() => {
  if (!props.targetAmount || props.targetAmount === 0) {
    return 0;
  }
  const percentage = (props.currentAmount / props.targetAmount) * 100;
  return Math.round(percentage);
});

// バーの表示幅（100%を超える場合は100%に制限）
const progressBarWidth = computed(() => {
  return Math.min(progressPercentage.value, 100);
});

// 金額をフォーマット
const formatCurrency = (amount) => {
  return new Intl.NumberFormat("ja-JP").format(amount);
};
</script>

<template>
  <div class="progress-section">
    <div class="progress-bar-container">
      <div
        class="progress-bar"
        :class="{ 'pulse-glow': isPulsing }"
        :style="{
          width: progressBarWidth + '%',
        }"
      >
        <span class="progress-text">{{ progressPercentage }}%</span>
      </div>
    </div>
    <div class="amount-display">
      <span class="currency"> 応援総額 </span> {{ formatCurrency(props.currentAmount)
      }}<span class="currency">円</span>
    </div>
  </div>
</template>

<style scoped>
.progress-section {
  width: 100%;
  padding: 10px 15px;
  /* background: rgba(0, 0, 0, 0.3); */
  border-radius: 10px;
  margin-bottom: 10px;
}

.progress-bar-container {
  width: 100%;
  height: 26px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 13px;
  overflow: hidden;
  margin-bottom: 8px;
  position: relative;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #ffdfea 0%, #ff299d 65%, #b000c7 100%);
  border-radius: 13px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 10px;
  transition: width 0.3s ease;
  position: relative;
  overflow: hidden;
  animation: progress-shimmer 3s infinite;
  transform-origin: left center;
}

/* ピカピカしながら拡大縮小するエフェクト */
.progress-bar.pulse-glow {
  animation: progress-shimmer 3s infinite, pulse-scale 5s ease-in-out, pulse-glow 5s ease-in-out;
}

.progress-bar::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  animation: shine 3s infinite;
}

@keyframes shine {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

@keyframes progress-shimmer {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.9;
  }
}

/* 拡大縮小アニメーション */
@keyframes pulse-scale {
  0% {
    transform: scaleX(1) scaleY(1);
  }
  25% {
    transform: scaleX(1.15) scaleY(1.25);
  }
  50% {
    transform: scaleX(0.95) scaleY(0.9);
  }
  75% {
    transform: scaleX(1.12) scaleY(1.2);
  }
  100% {
    transform: scaleX(1) scaleY(1);
  }
}

/* ピカピカ光るアニメーション */
@keyframes pulse-glow {
  0% {
    box-shadow: 0 0 5px rgba(255, 41, 157, 0.5), 0 0 10px rgba(176, 0, 199, 0.3);
    filter: brightness(1);
  }
  25% {
    box-shadow: 0 0 20px rgba(255, 41, 157, 1), 0 0 40px rgba(176, 0, 199, 0.8),
      0 0 60px rgba(255, 255, 255, 0.6);
    filter: brightness(1.3);
  }
  50% {
    box-shadow: 0 0 10px rgba(255, 41, 157, 0.7), 0 0 20px rgba(176, 0, 199, 0.5);
    filter: brightness(1.1);
  }
  75% {
    box-shadow: 0 0 25px rgba(255, 41, 157, 1), 0 0 50px rgba(176, 0, 199, 0.9),
      0 0 70px rgba(255, 255, 255, 0.7);
    filter: brightness(1.4);
  }
  100% {
    box-shadow: 0 0 5px rgba(255, 41, 157, 0.5), 0 0 10px rgba(176, 0, 199, 0.3);
    filter: brightness(1);
  }
}

.progress-text {
  color: white;
  font-weight: bold;
  font-size: 0.9rem;
  z-index: 1;
}

.amount-display {
  font-size: 18px;
  font-weight: bold;
  color: white;
  text-align: left;
  filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.5));
}

.currency {
  font-size: 0.9rem;
  margin-left: 0.2rem;
}

@media (min-width: 960px) {
  .progress-section {
    padding: 15px 20px;
    background: rgba(0, 0, 0, 0.3);
  }

  .amount-display {
    font-size: 20px;
    text-align: left;
  }
}
</style>
